import React, { Fragment, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { ReportsMenuStructure } from "../../menuStructures";
import { API_STATE } from "../../constants/api";
import {
  Pagination,
  ReportControls,
  LedgerReport,
} from "../../components/reports";
import {
  CurrencyRenderer,
  TextInputFilter,
} from "../../components/reports/CellTypes";
import { useReportsDetails } from "../../hooks/reportsHooks";
import { useStoreDetails } from "../../hooks/storeHooks";
import { useShowError } from "../../hooks/errorHooks";
import { rowPerPageOptions } from "../../constants/reports";
import { floatReportCSVHeaders } from "../../constants/reportCSVHeaders";
import SetActiveMenu from "../../menuStructures/SetActiveMenu";
import { useFetchEndOfDayReports } from "../../hooks/reports/useFetchEndOfDayReports";
import { addMultipleLocalizedDates } from "../../transformers/helpers/addLocalizedDate";
import { Table, Column, Cell } from "rsuite-table";
import "rsuite-table/dist/css/rsuite-table.css";
import "../../../css/rstable-overwrites.scss";
import CustomHeaderCell from "../../components/reports/CustomHeader";

function FloatReport() {
  const {
    apiState,
    currentPage,
    filterValues,
    overrideTableHeight,
    rowsPerPage,
    sortColumn,
    tableData,
    computedTableData,
    reset,
    setApiState,
    setCurrentPage,
    setTableRowsPerPage,
    setFilters,
    setSortingColumn,
    setSourceDataDetails,
    updateFilterValue,
  } = useReportsDetails();
  const tableRef = useRef<HTMLElement>();
  const modalRef = useRef<HTMLElement>();
  const { currencyCode } = useStoreDetails();
  const showError = useShowError();
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [availableTenders, setAvailableTenders] = useState([]);
  const [timeZone, setTimeZone] = useState<string>(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [showLedger, setShowLedger] = useState<string>();

  const {
    data: sourceData,
    isFetching,
    error,
  } = useFetchEndOfDayReports(
    {
      startDate,
      endDate,
    },
    {
      enabled: !!startDate && !!endDate,
    }
  );

  useEffect(() => {
    document.title = "Reports | BinderPOS";
    reset();
    setFilters(filters);
    setSortingColumn("openedDate", "desc");
  }, []);

  useEffect(() => {
    if (isFetching) {
      setApiState(API_STATE.LOADING);
    } else if (error) {
      showError(
        error as any,
        "Failed to load report",
        "There was an error retrieving your float report. Please try again"
      );
      setApiState(API_STATE.ERROR);
    } else if (sourceData) {
      setSourceDataDetails(sourceData?.report);
      setAvailableTenders(sourceData?.tenders);
      setApiState(API_STATE.SUCCESS);
    }
  }, [isFetching, sourceData, error]);

  useEffect(() => {
    if (showLedger) {
      modalRef.current?.focus();
    } else {
      tableRef.current?.focus();
    }
  }, [showLedger]);

  const filters = [
    {
      name: "till name",
      condition: (filterValues) => filterValues?.name?.length,
      filterFunction: (row, filterValues) =>
        row.name?.toLowerCase().includes(filterValues?.name?.toLowerCase()),
    },
  ];

  const handleSetDateRange = (fromDate, toDate, tz) => {
    setStartDate(fromDate);
    setEndDate(toDate);
    setTimeZone(tz);
  };

  const csvFilename = "Float Report.csv";

  const data = addMultipleLocalizedDates(computedTableData(), timeZone, [
    "openedDate",
    "closedDate",
  ]);

  return (
    <Fragment>
      <SetActiveMenu menuStructure={ReportsMenuStructure} />
      <div className="app-header">
        <h1 className="title">
          <span className="header-text">Float Report</span>
        </h1>
      </div>
      <div className="report">
        <ReportControls
          setDateRange={handleSetDateRange}
          csvHeaders={floatReportCSVHeaders(availableTenders)}
          csvFilename={csvFilename}
        />
        {apiState !== API_STATE.INITIAL && (
          <div className="data-table">
            <div className="table-container">
              <Table
                data={data}
                headerHeight={80}
                sortColumn={sortColumn.key}
                sortType={sortColumn.order}
                onSortColumn={setSortingColumn}
                renderEmpty={() => <div>No data to display</div>}
                loading={apiState == API_STATE.LOADING}
                className="rs-table--multi-header"
                fillHeight={overrideTableHeight === 0}
              >
                <Column key="col_actionLedger" width={75}>
                  <CustomHeaderCell title="Ledger" />
                  <Cell dataKey="floatId">
                    {(rowData) => (
                      <button
                        onClick={(event) => {
                          tableRef.current = event.currentTarget;
                          setShowLedger(rowData.floatId);
                        }}
                        className="button"
                      >
                        Ledger
                      </button>
                    )}
                  </Cell>
                </Column>
                <Column key="col_till_name" width={200} sortable resizable>
                  <CustomHeaderCell title="Name">
                    <TextInputFilter
                      aria-label="Filter by Till name"
                      title="Till name"
                      value={filterValues?.name}
                      setValue={(value) => updateFilterValue("name", value)}
                    />
                  </CustomHeaderCell>
                  <Cell dataKey="name" />
                </Column>
                <Column key="col_opened_date" width={200} sortable resizable>
                  <CustomHeaderCell title="Opening Time" />
                  <Cell dataKey="openedDate">
                    {(rowData) => rowData.openedDate_localized}
                  </Cell>
                </Column>
                <Column key="col_closed_date" width={200} sortable resizable>
                  <CustomHeaderCell title="Closing Time" />
                  <Cell dataKey="closedDate">
                    {(rowData) => rowData.closedDate_localized}
                  </Cell>
                </Column>
                {availableTenders?.map((tender) => (
                  <>
                    {tender !== "Store_Credit" && (
                      <Column
                        key={`col_${tender}_opening`}
                        width={150}
                        align="right"
                        sortable
                        resizable
                      >
                        <CustomHeaderCell title={`${tender} Opening`} />
                        <Cell dataKey={`${tender}.openingAmount`}>
                          {(rowData) =>
                            CurrencyRenderer({
                              cellData: rowData?.[tender]?.openingAmount,
                              currency: currencyCode,
                            })
                          }
                        </Cell>
                      </Column>
                    )}
                    <Column
                      key={`col_${tender}_current`}
                      width={150}
                      align="right"
                      sortable
                      resizable
                    >
                      <CustomHeaderCell title={`${tender} Current`} />
                      <Cell dataKey={`${tender}.currentAmount`}>
                        {(rowData) =>
                          CurrencyRenderer({
                            cellData: rowData?.[tender]?.currentAmount,
                            currency: currencyCode,
                          })
                        }
                      </Cell>
                    </Column>
                    {tender !== "Store_Credit" && (
                      <>
                        <Column
                          key={`col_${tender}_diff`}
                          width={150}
                          align="right"
                          sortable
                          resizable
                        >
                          <CustomHeaderCell title={`${tender} Diff`} />
                          <Cell dataKey={`${tender}.difference`}>
                            {(rowData) =>
                              CurrencyRenderer({
                                cellData: rowData?.[tender]?.difference,
                                currency: currencyCode,
                              })
                            }
                          </Cell>
                        </Column>
                        <Column
                          key={`col_${tender}_closing`}
                          width={150}
                          align="right"
                          sortable
                          resizable
                        >
                          <CustomHeaderCell title={`${tender} Closing`} />
                          <Cell dataKey={`${tender}.closingAmount`}>
                            {(rowData) =>
                              CurrencyRenderer({
                                cellData: rowData?.[tender]?.closingAmount,
                                currency: currencyCode,
                              })
                            }
                          </Cell>
                        </Column>
                      </>
                    )}
                  </>
                ))}
              </Table>
            </div>
            <Pagination
              apiState={apiState}
              currentPage={currentPage}
              totalRowCount={tableData.length}
              rowsPerPage={rowsPerPage}
              rowPerPageOptions={rowPerPageOptions}
              setCurrentPage={setCurrentPage}
              setRowsPerPage={setTableRowsPerPage}
            />
          </div>
        )}
      </div>
      {!!showLedger && (
        <LedgerReport
          id={showLedger}
          timeZone={timeZone}
          currencyCode={currencyCode}
          onClose={() => setShowLedger(undefined)}
          ref={modalRef}
        />
      )}
    </Fragment>
  );
}

FloatReport.propTypes = { store: PropTypes.object };
export default FloatReport;
